import React from 'react';
import classnames from 'classnames';

import BackButton from '../backButton/backButton';
import { Industries } from '../../modules/industries';
import ViewHeader from '../../components/viewHeader/viewHeader';

import {
  Button,
  ButtonSubtle,
  Divider,
  Input,
  RadioButton,
  SelectWithChildren as Select,
} from '@jimdo/ui';

import './industry.scss';
import { trackDWH } from '../../modules/tracking';

interface Props {
  backButtonLabel: string;
  cta: string;
  titleText: string;
  headline: string;
  skipLabel: string;
  selectDefault: string;
  industry: string;
  industries: Industries;
  placeholder: string;
  onBackClick: () => void;
  onSelect: (industry: string) => void;
  onSubmit: () => void;
  onSkip: () => void;
  setOther: (other: string) => void;
  jumpToIndustryView: boolean;
}

interface IndustryType {
  id: string;
  name: string;
}

export default class Industry extends React.Component<Props, {}> {
  constructor(props: Props) {
    super(props);
    this.handleChange = this.handleChange.bind(this);
    this.handleOther = this.handleOther.bind(this);
    this.handleBack = this.handleBack.bind(this);
    this.handleSkip = this.handleSkip.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  // tslint:disable max-func-body-length
  public render(): JSX.Element {
    const {
      cta,
      titleText,
      backButtonLabel,
      skipLabel,
      headline,
      selectDefault,
      industry,
      industries,
      placeholder,
      jumpToIndustryView,
    } = this.props;

    const industryList = Object.keys(industries).map((key: string, id: number) => {
      const shouldRender = industry === 'other' && key === 'other';
      const isSelected = industry === key;
      const classes = classnames('industry-component__radio', {
        'industry-component__radio--active': isSelected,
      });

      return (
        <RadioButton
          key={key}
          className={classes}
          name="INDUSTRY_OPTION"
          value={key || ''}
          checked={isSelected}
          onChange={this.handleChange}
        >
          {shouldRender ? (
            <Input
              type="text"
              name="industry-other"
              placeholder={placeholder}
              onChange={this.handleOther}
            />
          ) : (
            industries[key]
          )}
        </RadioButton>
      );
    });

    let industrySelectionMobile = this.getIndustryListForMobile(industries).map(
      ({ name, id }: IndustryType) => {
        return { value: id, text: name };
      }
    );

    industrySelectionMobile.unshift({ value: '', text: selectDefault });

    return (
      <div className="industry-component">
        {!jumpToIndustryView ? (
          <BackButton
            className="industry-component__button--desktop"
            onClick={this.handleBack}
            label={backButtonLabel}
          />
        ) : null}
        <div className="industry-component__content">
          <ViewHeader title={titleText} headlineText={headline} />
          <BackButton
            className="industry-component__button--mobile"
            onClick={this.handleBack}
            label={backButtonLabel}
          />
          <div className="industry-component__list">
            <div className="industry-component__desktop-selection">{industryList}</div>
            <Select
              innerWrapperClassName="industry-component__mobile-selection"
              onChange={this.handleChange}
              name="industry-selection"
              value={industry || ''}
            >
              {industrySelectionMobile.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.text}
                </option>
              ))}
            </Select>

            {industry === 'other' ? (
              <Input
                type="text"
                name="industry-other"
                placeholder={placeholder}
                className="industry-component__input--mobile"
              />
            ) : null}

            <Divider noGutter={true} />

            <div className="industry-component__footer">
              <ButtonSubtle
                type="button"
                className="industry-component__skip-button--desktop"
                onClick={this.handleSkip}
                noGutter={true}
              >
                {skipLabel}
              </ButtonSubtle>
              <Button
                disabled={!industry}
                onClick={this.handleSubmit}
                buttonStyle="primary"
                noGutter={true}
                className="industry-component__industry-button"
              >
                {cta}
              </Button>
            </div>
          </div>
        </div>
        <ButtonSubtle
          type="button"
          className="industry-component__skip-button--mobile"
          onClick={this.handleSkip}
        >
          {skipLabel}
        </ButtonSubtle>
      </div>
    );
  }
  // tslint:enable max-func-body-length

  private handleChange(event: React.SyntheticEvent<HTMLSelectElement | HTMLInputElement>): void {
    const elem = event.currentTarget;
    this.props.onSelect(elem.value);
  }

  private handleOther(event: React.ChangeEvent<HTMLInputElement>): void {
    const input = event.target;
    this.props.setOther(input.value);
  }

  private handleBack(): void {
    trackDWH('back-button', 'click', { currentStep: 'industry-view' });
    this.props.onBackClick();
  }

  private handleSubmit(): void {
    this.props.onSubmit();
  }

  private handleSkip(): void {
    this.props.onSkip();
  }

  private getIndustryListForMobile(industries: Industries): IndustryType[] {
    const { showAll, other } = industries;
    const baseArr = [
      {
        id: 'showAll',
        name: showAll,
      },
    ];
    return baseArr
      .concat(
        Object.keys(industries)
          .filter((id: string) => id !== 'showAll' && id !== 'other')
          .map((id: string) => ({
            name: industries[id],
            id,
          }))
      )
      .concat([
        {
          id: 'other',
          name: other,
        },
      ]);
  }
}
